import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader } from "semantic-ui-react";
import { getUserId } from "@/api/localStorage";

import SleepingCustomer from "./SleepingCustomer";
import i18n from "@/i18n";
import { CategoryT, ChangeServiceT } from "@/changeservice/components/MissingData/types";
import { fetchCategoryBySlug } from "@/api";
import { getTariff } from "@/api/tariff";
import type { Tariff } from "@/@types/tariff";
import { getSingleChangeService, getSleepingCustomerFields, updateSleepingCustomerFields } from "@/api/changeservice";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import Styles from "./SleepingCustomer.module.scss";
import { wait } from "@/changeservice/misc/utils";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const merge = (missingDataDetails: any, missingDataProvided: any) =>
  Object.keys(missingDataDetails).reduce(
    (details, field) => ({
      ...details,
      [field]: missingDataProvided?.[field] ?? null,
    }),
    {}
  );

export type SleepingCustomerT = {
  contractEndDate?: string | null | undefined;
  startOfDeliveryDate?: string | null | undefined;
  customerNumber?: string | null | undefined;
};

export type SleepingCustomerAddress = {
  postalcode: string;
  location: string;
  street: string;
  streetNumber: string;
};

const ConnectedSleepingCustomer = () => {
  const { category: categorySlug, tariffId } = useParams();
  const [category, setCategory] = useState<CategoryT>();
  const [tariff, setTariff] = useState<Tariff>();
  const [changeService, setChangeService] = useState<ChangeServiceT>();
  const [sleepingCustomer, setSleepingCustomer] = useState<SleepingCustomerT>();
  const [isUpdating, setIsUpdating] = useState(false);
  const userId = getUserId();
  const [error, setError] = useState<AxiosError>();

  const { t } = useTranslation(["sleepingCustomer"]);

  useEffect(() => {
    if (!categorySlug) return;
    const fetchCategory = async () => {
      const categoryResponse = await fetchCategoryBySlug(categorySlug);
      setCategory(categoryResponse);
    };
    fetchCategory();
  }, [categorySlug]);

  const fetchTariff = async () => {
    if (!tariffId) return;
    const tarifResponse = await getTariff(tariffId);
    setTariff(tarifResponse);
  };

  useEffect(() => {
    fetchTariff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tariffId]);

  useEffect(() => {
    if (!tariff) return;
    const fetchChangeService = async () => {
      try {
        const csResponse = await getSingleChangeService(tariff.userId, tariff.changeServiceId);
        setChangeService(csResponse);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setError(error);
      }
    };
    fetchChangeService();
  }, [tariff]);

  const fetchSleepingCustomer = async () => {
    try {
      if (!tariffId) return;
      const tarifResponse = await getSleepingCustomerFields(tariffId);
      const missingDataDetails = JSON.parse(tarifResponse.missingDataDetails);
      const missingDataProvided = JSON.parse(tarifResponse.missingDataProvided);
      setSleepingCustomer(merge(missingDataDetails, missingDataProvided) as Tariff);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setError(error);
    }
  };
  useEffect(() => {
    fetchSleepingCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tariffId]);

  const changeServiceId = tariff?.changeServiceId;
  const isCategoryLoaded = !!category;
  const isTariffLoaded = !!changeServiceId;
  const isChangeServiceLoaded = !!changeService;

  const isUserLoaded = !!userId;

  const isSleepingCustomerLoaded = !!sleepingCustomer;

  const isLoading =
    !isCategoryLoaded || !isTariffLoaded || !isUserLoaded || !isChangeServiceLoaded || !isSleepingCustomerLoaded;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSave = async (values: any) => {
    if (!values || !tariffId) return;
    setIsUpdating(true);
    try {
      await updateSleepingCustomerFields(tariffId, values);
      await wait(3000); // Simulates a delay, adjust as needed
      await fetchTariff();
      await fetchSleepingCustomer();
    } catch (error) {
      console.error("Failed to update customer fields:", error);
    } finally {
      setIsUpdating(false);
      window.location.reload();
    }
  };

  return isLoading ? (
    error ? (
      <div className={Styles.notFound}>
        <h3>{t("tariffNotFound")}</h3>
      </div>
    ) : (
      <Loader active data-testid="spinner" content={i18n.t("common.loading")} />
    )
  ) : (
    <SleepingCustomer
      sleepingCustomer={sleepingCustomer}
      category={category}
      address={{
        postalcode: changeService.postalcode || "",
        location: changeService.location || "",
        street: changeService.street || "",
        streetNumber: changeService.streetNumber || "",
      }}
      counterNumber={changeService?.counterNumber || ""}
      isUpdating={isUpdating}
      onSave={onSave}
      prevCustomerNumber={tariff.prevCustomerNumber}
      tarifName={tariff.newProviderName}
    />
  );
};

export default ConnectedSleepingCustomer;
